import type { AuthMeResponse } from '@/features/auth/api';
import type {
  TourDefinition,
  TourId,
  TourStateEntry,
  TourStep,
  TourStepId,
  ToursState,
} from './types';

/** In-memory session for a running tour (null when idle). */
export interface ActiveTour {
  tourId: TourId;
  /** Steps whose target existed at start, in order — frozen for this run. */
  steps: TourStep[];
  /** Index into `steps`. */
  index: number;
}

/** Keep only steps whose target is in the DOM right now, order preserved.
 *  Called at start/replay to freeze the present-step list. */
export function resolvePresentSteps(
  def: TourDefinition,
  isPresent: (target: string) => boolean,
): TourStep[] {
  return def.steps.filter((s) => isPresent(s.target));
}

/** Index of the saved step ID within the present list; 0 if absent. */
export function resumeIndex(steps: TourStep[], savedStepId: TourStepId | null): number {
  if (!savedStepId) return 0;
  const i = steps.findIndex((s) => s.id === savedStepId);
  return i === -1 ? 0 : i;
}

/** First-visit auto-fire gate: desktop, terms accepted, never seen before. */
export function shouldAutoStart(args: {
  entry: TourStateEntry | undefined;
  isDesktop: boolean;
  termsAccepted: boolean;
}): boolean {
  return args.isDesktop && args.termsAccepted && args.entry === undefined;
}

/** Merge one tour's state into a full `tours` map for the PATCH body (keeps
 *  any other saved tours). */
export function buildToursPatch(
  existing: ToursState | undefined,
  tourId: TourId,
  entry: TourStateEntry,
): { tours: ToursState } {
  return { tours: { ...(existing ?? {}), [tourId]: entry } };
}

/** Read the tours slice off a /me payload (safe when nothing saved yet). */
export function toursFromMe(me: AuthMeResponse | null | undefined): ToursState {
  return me?.ui_prefs.tours ?? {};
}
